/**
 * Product Category Question Generator
 *
 * Start question generation from product categories instead of pre-existing wheels.
 * User selects a product category, system auto-creates wheel and generates questions.
 *
 * Enhanced with product-specific Q&A generation (Phases A-F)
 *
 * Date: 2025-11-23
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sparkles,
  Play,
  AlertCircle,
  CheckCircle2,
  Package,
  Search,
  TrendingUp,
  DollarSign,
  Loader2,
  ArrowLeft,
  Globe,
  Zap,
  Database,
  Target,
  FileText,
  Download,
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useToast } from '@/components/ui/use-toast';

interface ProductCategory {
  category: string;
  productCount: number;
  brandCount: number;
  priceRange: {
    min: number;
    max: number;
    currency: string;
  } | null;
}

const QUESTION_TYPES = [
  { id: 'PC-PB', name: 'Product-Benefit Questions', defaultSelected: true },
  { id: 'PC-PA', name: 'Product-Attribute Questions', defaultSelected: true },
  { id: 'PC-TR', name: 'Product-Trend Questions', defaultSelected: false },
  { id: 'PC-PB-CMP', name: 'Product Comparison Questions', defaultSelected: false },
  { id: 'PC-BP-PB-CMP', name: 'Persona-Based Comparison Questions', defaultSelected: false },
  { id: 'PB-RANK', name: 'Rank by Benefit', defaultSelected: true },
  { id: 'BP-RANK', name: 'Rank by Persona', defaultSelected: false },
];

export default function ProductCategoryGenerator() {
  const { sectorId } = useParams<{ sectorId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<ProductCategory[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState<string[]>(
    QUESTION_TYPES.filter(qt => qt.defaultSelected).map(qt => qt.id)
  );
  const [selectedLanguage, setSelectedLanguage] = useState<string>('es');
  const [sectorName, setSectorName] = useState<string>('');

  // Product-specific Q&A generation state
  const [siteId, setSiteId] = useState<string | null>(null);
  const [wheelId, setWheelId] = useState<string | null>(null);
  const [showProductQA, setShowProductQA] = useState(false);
  const [productQAPhase, setProductQAPhase] = useState<string | null>(null);
  const [productQAProgress, setProductQAProgress] = useState(0);
  const [productQAStatus, setProductQAStatus] = useState<string>('');
  const [productQAResults, setProductQAResults] = useState<any>(null);

  useEffect(() => {
    loadData();
  }, [sectorId]);

  useEffect(() => {
    // Filter categories based on search term
    if (searchTerm.trim() === '') {
      setFilteredCategories(categories);
    } else {
      const filtered = categories.filter(cat =>
        cat.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCategories(filtered);
    }
  }, [searchTerm, categories]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load sector details
      const sectorResponse = await authFetch(`/api/sectors/${sectorId}`);
      const sectorData = await sectorResponse.json();
      setSectorName(sectorData.sector?.name || 'Unknown Sector');

      // Load associated sites for this sector
      const sitesResponse = await authFetch(`/api/sites?sector_id=${sectorId}`);
      const sitesData = await sitesResponse.json();
      if (sitesData.sites && sitesData.sites.length > 0) {
        setSiteId(sitesData.sites[0].id || sitesData.sites[0]._id);
      }

      // Load product categories
      const categoriesResponse = await authFetch(
        `/api/sectors/${sectorId}/product-categories?limit=200`
      );
      const categoriesData = await categoriesResponse.json();

      if (!categoriesData.success) {
        throw new Error(categoriesData.error || 'Failed to load product categories');
      }

      setCategories(categoriesData.categories || []);
      setFilteredCategories(categoriesData.categories || []);
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load product categories',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionTypeToggle = (questionTypeId: string) => {
    setSelectedQuestionTypes((prev) =>
      prev.includes(questionTypeId)
        ? prev.filter((id) => id !== questionTypeId)
        : [...prev, questionTypeId]
    );
  };

  // Product-specific Q&A Generation Functions
  const runPhaseA = async () => {
    if (!siteId || !selectedCategory) return;

    try {
      setProductQAPhase('Phase A: Attribute Normalization');
      setProductQAStatus('Normalizing product attributes...');
      setProductQAProgress(10);

      const response = await authFetch(`/api/sites/${siteId}/normalize-attributes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categoryName: selectedCategory }),
      });

      const data = await response.json();
      setProductQAProgress(20);

      if (!data.success) {
        throw new Error(data.error || 'Phase A failed');
      }

      setProductQAStatus(`Phase A complete: ${data.stats.totalMappings} attribute mappings created`);
      return data;
    } catch (error: any) {
      console.error('Phase A error:', error);
      throw error;
    }
  };

  const runPhaseB = async () => {
    if (!siteId || !selectedCategory) return;

    try {
      setProductQAPhase('Phase B: Product Benefit Ranking');
      setProductQAStatus('Ranking products by perceived benefits...');
      setProductQAProgress(30);

      // Get benefits from sector research
      const benefitsResponse = await authFetch(`/api/sectors/${sectorId}`);
      const benefitsData = await benefitsResponse.json();
      const benefits = benefitsData.sector?.research_results?.benefits || [];

      if (benefits.length === 0) {
        setProductQAStatus('Phase B skipped: No benefits found in sector research');
        setProductQAProgress(40);
        return null;
      }

      // Rank by first benefit (can be extended to rank by all)
      const benefitName = benefits[0]?.name || benefits[0];

      const response = await authFetch(`/api/sites/${siteId}/rank-products-by-benefit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          categoryName: selectedCategory,
          benefitName: benefitName,
        }),
      });

      const data = await response.json();
      setProductQAProgress(40);

      if (!data.success) {
        throw new Error(data.error || 'Phase B failed');
      }

      setProductQAStatus(`Phase B complete: ${data.stats.productsRanked} products ranked by "${benefitName}"`);
      return data;
    } catch (error: any) {
      console.error('Phase B error:', error);
      throw error;
    }
  };

  const runPhaseCD = async (generatedWheelId: string) => {
    if (!siteId) return;

    try {
      setProductQAPhase('Phase C+D: Product Question Generation');
      setProductQAStatus('Mapping questions to products and reformulating...');
      setProductQAProgress(50);

      const response = await authFetch(
        `/api/sites/${siteId}/question-wheels/${generatedWheelId}/generate-product-questions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ limitProducts: 100 }),
        }
      );

      const data = await response.json();
      setProductQAProgress(80);

      if (!data.success) {
        throw new Error(data.error || 'Phase C+D failed');
      }

      setProductQAStatus(`Phase C+D complete: ${data.stats.questionsGenerated} product-specific questions created`);
      return data;
    } catch (error: any) {
      console.error('Phase C+D error:', error);
      throw error;
    }
  };

  const handleRunProductQA = async (batchJobId: string, generatedWheelId: string) => {
    try {
      setShowProductQA(true);
      setProductQAProgress(0);
      setProductQAPhase('Starting');
      setProductQAStatus('Initializing product-specific Q&A generation...');
      setWheelId(generatedWheelId);

      // Run phases sequentially
      const phaseAResults = await runPhaseA();
      const phaseBResults = await runPhaseB();
      const phaseCDResults = await runPhaseCD(generatedWheelId);

      setProductQAProgress(100);
      setProductQAPhase('Complete');
      setProductQAStatus('All phases complete! Product-specific questions are ready for review.');
      setProductQAResults({
        phaseA: phaseAResults,
        phaseB: phaseBResults,
        phaseCD: phaseCDResults,
      });

      toast({
        title: 'Product Q&A Generation Complete',
        description: 'Product-specific questions have been generated and are ready for review.',
      });
    } catch (error: any) {
      console.error('Product Q&A generation error:', error);
      setProductQAStatus(`Error: ${error.message}`);
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate product-specific Q&A',
        variant: 'destructive',
      });
    }
  };

  const handleGenerate = async (runProductQAAfter: boolean = false) => {
    if (!selectedCategory) {
      toast({
        title: 'Validation Error',
        description: 'Please select a product category',
        variant: 'destructive',
      });
      return;
    }

    if (selectedQuestionTypes.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one question type',
        variant: 'destructive',
      });
      return;
    }

    try {
      setGenerating(true);

      const response = await authFetch(
        `/api/sectors/${sectorId}/generate-from-product-category`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            category: selectedCategory,
            language: selectedLanguage,
            questionTypes: selectedQuestionTypes,
            parameters: {}, // Could add custom parameters here
          }),
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to start generation');
      }

      toast({
        title: 'Sector Question Wheel Created',
        description: data.message,
      });

      // If product Q&A requested, run it automatically
      if (runProductQAAfter && data.wheelId) {
        await handleRunProductQA(data.batchJobId, data.wheelId);
      } else {
        // Navigate to batch job monitor
        navigate(`/sectors/${sectorId}/question-generation/monitor/${data.batchJobId}`);
      }
    } catch (error: any) {
      console.error('Error starting generation:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to start generation',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  const getSimplifiedCategoryName = (category: string) => {
    // Extract the last part of Google category paths
    return category.includes('>') ? category.split('>').pop()?.trim() || category : category;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/admin/sectors`)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Generate from Product Category</h1>
              <p className="text-muted-foreground mt-2">
                Select a product category to auto-create a question wheel for {sectorName}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Product Categories */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Select Product Category
          </CardTitle>
          <CardDescription>
            Choose a category from your product feed ({categories.length} categories available)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Category List */}
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {filteredCategories.length === 0 ? (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {searchTerm ? 'No categories match your search' : 'No product categories found'}
                </AlertDescription>
              </Alert>
            ) : (
              filteredCategories.map((cat, idx) => (
                <div
                  key={idx}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedCategory === cat.category
                      ? 'border-primary bg-primary/5 dark:bg-primary/10'
                      : 'hover:bg-accent dark:hover:bg-gray-700/50'
                  }`}
                  onClick={() => setSelectedCategory(cat.category)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-sm mb-1">
                        {getSimplifiedCategoryName(cat.category)}
                      </div>
                      {cat.category.includes('>') && (
                        <div className="text-xs text-muted-foreground mb-2">
                          {cat.category}
                        </div>
                      )}
                      <div className="flex gap-2 flex-wrap">
                        <Badge variant="secondary" className="text-xs">
                          <Package className="h-3 w-3 mr-1" />
                          {cat.productCount} products
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          <TrendingUp className="h-3 w-3 mr-1" />
                          {cat.brandCount} brands
                        </Badge>
                        {cat.priceRange && (
                          <Badge variant="outline" className="text-xs">
                            <DollarSign className="h-3 w-3 mr-1" />
                            {cat.priceRange.min.toFixed(2)} - {cat.priceRange.max.toFixed(2)}{' '}
                            {cat.priceRange.currency}
                          </Badge>
                        )}
                      </div>
                    </div>
                    {selectedCategory === cat.category && (
                      <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 ml-2" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Language Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Question Language
          </CardTitle>
          <CardDescription>
            Select the language for generated questions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select language" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="es">Spanish (Español)</SelectItem>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="de">German (Deutsch)</SelectItem>
              <SelectItem value="fr">French (Français)</SelectItem>
              <SelectItem value="it">Italian (Italiano)</SelectItem>
              <SelectItem value="pt">Portuguese (Português)</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Question Types */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Question Types to Generate
          </CardTitle>
          <CardDescription>
            Select which types of questions to generate ({selectedQuestionTypes.length} selected)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {QUESTION_TYPES.map((qt) => (
              <div
                key={qt.id}
                className="flex items-start space-x-3 p-3 rounded-lg hover:bg-accent dark:hover:bg-gray-700/50 cursor-pointer"
                onClick={() => handleQuestionTypeToggle(qt.id)}
              >
                <Checkbox
                  id={`qt-${qt.id}`}
                  checked={selectedQuestionTypes.includes(qt.id)}
                  onCheckedChange={() => handleQuestionTypeToggle(qt.id)}
                />
                <div className="flex-1">
                  <Label htmlFor={`qt-${qt.id}`} className="font-medium cursor-pointer">
                    {qt.name}
                  </Label>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Product-Specific Q&A Generation */}
      {siteId && (
        <Card className="border-primary/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              Product-Specific Q&A Generation (NEW!)
            </CardTitle>
            <CardDescription>
              Enhanced workflow: Create sector wheel + generate product-specific questions automatically
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <Sparkles className="h-4 w-4" />
              <AlertDescription>
                This complete workflow will:
                <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                  <li><strong>Phase A</strong>: Normalize product attributes across all products</li>
                  <li><strong>Phase B</strong>: Rank products by perceived benefits</li>
                  <li><strong>Phase C+D</strong>: Generate product-specific questions from sector wheel</li>
                </ul>
              </AlertDescription>
            </Alert>

            {showProductQA && (
              <div className="space-y-3 p-4 bg-accent/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">{productQAPhase}</div>
                  <div className="text-sm text-muted-foreground">{productQAProgress}%</div>
                </div>
                <Progress value={productQAProgress} className="h-2" />
                <div className="text-xs text-muted-foreground">{productQAStatus}</div>

                {productQAResults && (
                  <div className="mt-4 space-y-2">
                    <div className="font-medium text-sm">Results:</div>
                    <div className="grid gap-2">
                      {productQAResults.phaseA && (
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          <span>
                            <strong>Phase A:</strong> {productQAResults.phaseA.stats?.totalMappings || 0} attribute mappings
                          </span>
                        </div>
                      )}
                      {productQAResults.phaseB && (
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          <span>
                            <strong>Phase B:</strong> {productQAResults.phaseB.stats?.productsRanked || 0} products ranked
                          </span>
                        </div>
                      )}
                      {productQAResults.phaseCD && (
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          <span>
                            <strong>Phase C+D:</strong> {productQAResults.phaseCD.stats?.questionsGenerated || 0} questions generated
                          </span>
                        </div>
                      )}
                    </div>

                    <Button
                      className="mt-4 w-full"
                      onClick={() => navigate(`/admin/question-wheel-debug`)}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      Review Generated Questions
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <Card>
        <CardHeader>
          <CardTitle>Ready to Generate</CardTitle>
          <CardDescription>
            Choose your generation mode
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {selectedCategory && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                Will create wheel: <strong>{getSimplifiedCategoryName(selectedCategory)}</strong>
                <br />
                Question types: {selectedQuestionTypes.join(', ')}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col gap-3">
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => navigate(`/admin/sectors`)}
                disabled={generating}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={() => handleGenerate(false)}
                disabled={generating || !selectedCategory || selectedQuestionTypes.length === 0}
              >
                {generating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Sector Wheel Only
                  </>
                )}
              </Button>
              {siteId && (
                <Button
                  onClick={() => handleGenerate(true)}
                  disabled={generating || !selectedCategory || selectedQuestionTypes.length === 0}
                  className="bg-primary"
                >
                  {generating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Running Complete Workflow...
                    </>
                  ) : (
                    <>
                      <Zap className="mr-2 h-4 w-4" />
                      Complete Workflow (Wheel + Product Q&A)
                    </>
                  )}
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground text-right">
              💡 Tip: Use "Complete Workflow" to automatically generate product-specific questions
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
